import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../lib/hooks'
import { MfaChallenge } from '../components/MfaChallenge'
import { ForcePasswordChange } from '../components/ForcePasswordChange'

/**
 * Split-Screen Login mit 3 Steps:
 *  step.kind = 'password'      → Passwort-Form (default)
 *  step.kind = 'mfa'           → TOTP/Backup-Code-Eingabe nach Passwort-OK
 *  step.kind = 'force_change'  → neue Passwort-Eingabe (Rotation überschritten)
 */
type Step =
  | { kind: 'password' }
  | { kind: 'mfa'; challengeToken: string }
  | { kind: 'force_change'; changeToken: string }

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [step, setStep] = useState<Step>({ kind: 'password' })
  const { login, isAuthenticated, loading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [loading, isAuthenticated, navigate])

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setError('')
    setSubmitting(true)
    try {
      const result = await login(email, password)
      if (result.kind === 'session') {
        // AuthProvider hat user gesetzt → der useEffect oben triggert navigate
        return
      }
      if (result.kind === 'mfa_required') {
        setStep({ kind: 'mfa', challengeToken: result.challengeToken })
      } else if (result.kind === 'password_change_required') {
        setStep({ kind: 'force_change', changeToken: result.changeToken })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 420px) 1fr',
        minHeight: '100dvh',
        background: 'var(--paper)',
      }}
      className="login-grid"
    >
      {/* ─── Brand-Panel (links) ───────────────────────────────── */}
      <aside
        style={{
          background: 'var(--sb-sidebar-bg)',
          color: 'var(--sb-sidebar-fg)',
          padding: '40px 32px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
        className="login-brand"
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 5,
                background: 'var(--sb-sidebar-fg-active)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0a0a0a',
                fontFamily: 'var(--mono)',
                fontWeight: 700,
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              &gt;
            </span>
            <div>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: 'var(--sb-sidebar-fg)',
                }}
              >
                Werkgesell
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: 'var(--sb-sidebar-fg-muted)',
                  marginTop: 3,
                  letterSpacing: '0.05em',
                }}
              >
                magita · v2.4.1
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 56,
              fontSize: 22,
              lineHeight: 1.35,
              color: 'var(--sb-sidebar-fg)',
              fontWeight: 500,
              letterSpacing: '-0.015em',
            }}
          >
            Multi-Channel<br />
            Financial Reporting<br />
            <span style={{ color: 'var(--sb-sidebar-fg-active)' }}>+ Simulation</span>
          </div>
          <p
            style={{
              marginTop: 18,
              fontSize: 13,
              color: 'var(--sb-sidebar-fg-muted)',
              maxWidth: 280,
              lineHeight: 1.55,
            }}
          >
            Amazon · Bol · eBay · Kaufland · Galaxus · Otto · Shopify —
            ein Dashboard, eine Wahrheit.
          </p>
        </div>

        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--sb-sidebar-fg-muted)',
            letterSpacing: '0.06em',
          }}
        >
          BETA · single-tenant · MFA-protected
        </div>
      </aside>

      {/* ─── Form-Panel (rechts) ───────────────────────────────── */}
      <main
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 32px',
        }}
      >
        {step.kind === 'password' && (
          <PasswordForm
            email={email}
            password={password}
            error={error}
            submitting={submitting}
            onEmailChange={setEmail}
            onPasswordChange={setPassword}
            onSubmit={handlePasswordSubmit}
          />
        )}

        {step.kind === 'mfa' && (
          <MfaChallenge
            challengeToken={step.challengeToken}
            onSession={() => navigate('/', { replace: true })}
            onPasswordChangeRequired={(token) =>
              setStep({ kind: 'force_change', changeToken: token })
            }
            onCancel={() => {
              setStep({ kind: 'password' })
              setPassword('')
              setError('')
            }}
          />
        )}

        {step.kind === 'force_change' && (
          <ForcePasswordChange
            changeToken={step.changeToken}
            onSession={() => navigate('/', { replace: true })}
            onCancel={() => {
              setStep({ kind: 'password' })
              setPassword('')
              setError('')
            }}
          />
        )}
      </main>

      {/* Mobile: stack vertikal, Brand wird kompakter Header */}
      <style>{`
        @media (max-width: 720px) {
          .login-grid {
            grid-template-columns: 1fr !important;
            grid-template-rows: auto 1fr;
          }
          .login-brand {
            padding: 22px 20px !important;
          }
          .login-brand > div:first-child > div:nth-child(2) {
            display: none;
          }
        }
      `}</style>
    </div>
  )
}

interface PasswordFormProps {
  email: string
  password: string
  error: string
  submitting: boolean
  onEmailChange: (v: string) => void
  onPasswordChange: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
}

function PasswordForm({
  email,
  password,
  error,
  submitting,
  onEmailChange,
  onPasswordChange,
  onSubmit,
}: PasswordFormProps) {
  return (
    <div style={{ width: '100%', maxWidth: 380 }}>
      <h1
        style={{
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: '-0.02em',
          color: 'var(--ink)',
          margin: '0 0 6px',
        }}
      >
        Anmelden
      </h1>
      <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: '0 0 28px' }}>
        Mit deinem Werkgesell-Account einloggen.
      </p>

      {error && (
        <div
          role="alert"
          style={{
            marginBottom: 18,
            padding: '10px 12px',
            background: 'var(--bad-bg)',
            color: 'var(--bad)',
            border: '1px solid color-mix(in srgb, var(--bad) 30%, transparent)',
            borderRadius: 8,
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={onSubmit} noValidate>
        <Field
          id="login-email"
          label="Email"
          type="email"
          autoComplete="username"
          value={email}
          onChange={onEmailChange}
          disabled={submitting}
          autoFocus
        />
        <Field
          id="login-password"
          label="Passwort"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={onPasswordChange}
          disabled={submitting}
        />

        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="sk-btn primary"
          style={{
            width: '100%',
            justifyContent: 'center',
            padding: '11px 16px',
            fontSize: 14,
            marginTop: 8,
          }}
        >
          {submitting ? 'Anmelden…' : 'Anmelden'}
        </button>
      </form>

      <p
        className="mono"
        style={{
          marginTop: 32,
          fontSize: 10,
          color: 'var(--ink-3)',
          letterSpacing: '0.06em',
        }}
      >
        HTTPS · HttpOnly-Cookie · TOTP-MFA · 10 failed attempts/min throttled
      </p>
    </div>
  )
}

interface FieldProps {
  id: string
  label: string
  type: 'email' | 'password' | 'text'
  autoComplete: string
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  autoFocus?: boolean
}

function Field({
  id,
  label,
  type,
  autoComplete,
  value,
  onChange,
  disabled,
  autoFocus,
}: FieldProps) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--ink-2)',
          marginBottom: 6,
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        autoFocus={autoFocus}
        required
        className="sk-input"
        style={{
          width: '100%',
          fontSize: 14,
          padding: '9px 12px',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
    </div>
  )
}
